import type { Result } from '../../../lib/api-types';

/**
 * Rank/progress share card (M8, spec §3.6): drawn on a canvas in the Mini App, so no server
 * rendering and no learner data leaves the phone unless the learner shares the image.
 * Shows only what the learner chooses to share: score, rank, percentile. No name or ID.
 */
export async function rankCard(
  data: Result,
  labels: { brand: string; cta: string },
): Promise<Blob> {
  const size = 1080;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (!g) throw new Error('canvas unavailable');
  const [logo] = await Promise.all([loadImage('/brand/logo-512.png'), loadFonts()]);

  // Brand backdrop: deep palette-C teal, light/amber glows, faint teal grid, amber hairline frame.
  g.fillStyle = '#0f5c70';
  g.fillRect(0, 0, size, size);
  const glow = (x: number, y: number, r: number, colour: string) => {
    const rg = g.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, colour);
    rg.addColorStop(1, 'rgba(15,92,112,0)');
    g.fillStyle = rg;
    g.fillRect(0, 0, size, size);
  };
  glow(size / 2, 300, 620, 'rgba(93,186,203,0.45)');
  glow(size, 0, 520, 'rgba(245,158,11,0.24)');
  glow(0, size, 520, 'rgba(15,118,110,0.4)');
  g.strokeStyle = 'rgba(94,234,212,0.06)';
  g.lineWidth = 1;
  for (let x = 0; x <= size; x += 54) {
    g.beginPath();
    g.moveTo(x, 0);
    g.lineTo(x, size);
    g.stroke();
    g.beginPath();
    g.moveTo(0, x);
    g.lineTo(size, x);
    g.stroke();
  }
  g.strokeStyle = 'rgba(252,211,77,0.55)';
  g.lineWidth = 3;
  roundRect(g, { x: 36, y: 36, w: size - 72, h: size - 72, r: 44 });
  g.stroke();

  if (logo) g.drawImage(logo, size / 2 - 120, 70, 240, 240);

  const amber = (y0: number, y1: number) => {
    const lg = g.createLinearGradient(0, y0, 0, y1);
    lg.addColorStop(0, '#fce7c0');
    lg.addColorStop(0.5, '#f5b957');
    lg.addColorStop(1, '#f2a93b');
    return lg;
  };
  g.textAlign = 'center';
  g.textBaseline = 'alphabetic';
  const line = (
    text: string,
    {
      y,
      px,
      weight = 600,
      fill = '#f4fafc',
      display = false,
    }: {
      y: number;
      px: number;
      weight?: number;
      fill?: string | CanvasGradient;
      display?: boolean;
    },
  ) => {
    const family = display ? DISPLAY : BODY;
    g.font = `${String(weight)} ${String(px)}px ${family}`;
    g.fillStyle = fill;
    let s = text;
    while (g.measureText(s).width > size - 160 && s.length > 4) s = `${s.slice(0, -2)}…`;
    g.fillText(s, size / 2, y);
  };
  line(labels.brand.toUpperCase(), { y: 360, px: 30, weight: 600, fill: '#f8cb7a' });
  line(data.mock.title, { y: 420, px: 46, weight: 700, fill: '#f4fafc', display: true });
  line(`${String(data.score.total)} / ${String(data.score.max)}`, {
    y: 590,
    px: 150,
    weight: 700,
    fill: amber(470, 600),
    display: true,
  });
  if (data.rank) {
    line(`AIR ${String(data.rank.rank)} of ${String(data.rank.cohort)}`, {
      y: 690,
      px: 66,
      weight: 700,
      fill: '#f4fafc',
      display: true,
    });
    line(`Percentile ${String(data.rank.percentile)}`, {
      y: 760,
      px: 44,
      weight: 500,
      fill: '#f8cb7a',
    });
  }
  line(
    `${String(data.score.correct)} right · ${String(data.score.wrong)} wrong · ${String(data.score.unanswered)} skipped`,
    { y: data.rank ? 830 : 700, px: 36, weight: 400, fill: '#d3e5ea' },
  );

  // CTA pill in the brand teal with an amber edge.
  g.font = `600 38px ${BODY}`;
  const w = Math.min(size - 200, g.measureText(labels.cta).width + 120);
  const pill = g.createLinearGradient(size / 2 - w / 2, 0, size / 2 + w / 2, 0);
  pill.addColorStop(0, '#1e8fa3');
  pill.addColorStop(1, '#177a8c');
  g.fillStyle = pill;
  roundRect(g, { x: size / 2 - w / 2, y: 900, w, h: 84, r: 42 });
  g.fill();
  g.strokeStyle = 'rgba(248,203,122,0.8)';
  g.lineWidth = 2;
  g.stroke();
  line(labels.cta, { y: 955, px: 38, weight: 600 });

  return new Promise((resolve, reject) => {
    c.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('could not render the card'));
    }, 'image/png');
  });
}

const DISPLAY = "'Space Grotesk Variable', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
const BODY = "'Inter Variable', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";

/** Make sure the self-hosted brand fonts are ready before drawing (falls back silently). */
async function loadFonts(): Promise<void> {
  try {
    await Promise.all([
      document.fonts.load(`700 150px ${DISPLAY}`),
      document.fonts.load(`600 38px ${BODY}`),
    ]);
  } catch {
    /* system font fallback */
  }
}

/** Same-origin logo for the card; the card still renders if it fails to load. */
function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = src;
  });
}

function roundRect(
  g: CanvasRenderingContext2D,
  { x, y, w, h, r }: { x: number; y: number; w: number; h: number; r: number },
): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** Share via the system sheet when possible; otherwise download the PNG. */
export async function shareCard(blob: Blob, filename: string): Promise<'shared' | 'downloaded'> {
  const file = new File([blob], filename, { type: 'image/png' });
  // Older WebViews have no canShare (calling it throws): fall back to a download.
  let canShare = false;
  try {
    canShare = navigator.canShare({ files: [file] });
  } catch {
    canShare = false;
  }
  if (canShare) {
    await navigator.share({ files: [file] });
    return 'shared';
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 5_000);
  return 'downloaded';
}
